import React from 'react'
import Stack from '@mui/material/Stack'
import LineChart from './LineChart'
import Grid from '@mui/material/Grid'
import PieChart from './PieChart'
import ResolutionPicker from './ResolutionPicker'
import { useState } from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/InfoOutlined'

interface Props {
  setError: React.Dispatch<any>
  ready?: Boolean
  scenario?: any
}

const getPvVsEldemandData = (lineData: any) => {
  const lines = []
  lines.push(lineData[0].find((el: any) => el.id.includes('eldemand')))
  lineData.forEach((arr: any, index: any) => {
    let obj = arr.find((el: any) => el?.id === 'pv_total_power')
    if (obj) obj.id = 'pv_total_power_' + index
    lines.push(obj)
  })
  return lines.reverse()
}

export function ScenarioDashboard({ ready, setError, scenario }: Props) {
  const [data, setData] = useState<any>(undefined)
  const [showPicker, setShowPicker] = useState<Boolean>(false)

  React.useEffect(() => {
    if (ready || scenario) setShowPicker(true)
  }, [ready, scenario])

  return (
    <>
      {showPicker && <ResolutionPicker setData={setData} setError={setError} scenario={scenario} />}
      {data?.kpis && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sx={{ m: 2, mb: 0 }}>
            <Typography variant='h6' sx={{ mb: 1 }}>
              KPI's
            </Typography>
            <Paper variant='outlined'>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Index</TableCell>
                      <TableCell align='right'>Total PV production</TableCell>
                      <TableCell align='right'>Total Electricity demand</TableCell>
                      <TableCell align='right'>Self consumption PV</TableCell>
                      <TableCell align='right'>
                        <Grid container justifyContent={'right'}>
                          <Grid item>Self consumption</Grid>
                          <Grid item>
                            <Tooltip
                              title={
                                'Self consumption based on PV output and cheap enegry from the grid stored in battery.'
                              }
                            >
                              <InfoIcon sx={{ fontSize: 16, ml: 0.5, mt: 0.2 }} />
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell align='right'>Battery storage grid</TableCell>
                      <TableCell align='right'>Battery storage solar</TableCell>
                      <TableCell align='right'>Total costs</TableCell>
                      <TableCell align='right'>Total savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.kpis.map((kpi: any, index: any) => (
                      <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell component='th' scope='row'>
                          {index}
                        </TableCell>
                        <TableCell align='right'>{`${(kpi.total_pv / 1000).toFixed(2)} kWh`}</TableCell>
                        <TableCell align='right'>{`${(kpi.total_eldemand / 1000).toFixed(2)} kWh`}</TableCell>
                        <TableCell align='right'>{`${(kpi.self_consumption_pv * 100).toFixed(2)} %`}</TableCell>
                        <TableCell align='right'>{`${(kpi.self_consumption * 100).toFixed(2)} %`}</TableCell>
                        <TableCell align='right'>{`${(kpi.battery_storage_grid / 1000).toFixed(2)} kWh`}</TableCell>
                        <TableCell align='right'>{`${(kpi.battery_storage_solar / 1000).toFixed(2)} kWh`}</TableCell>
                        <TableCell align='right'>{`${kpi.total_costs.toFixed(2)} €`}</TableCell>
                        <TableCell align='right'>{`${kpi.total_savings.toFixed(2)} €`}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
      {data?.lineData && (
        <Stack alignItems={'center'}>
          <LineChart
            colors={{ scheme: 'set1' }}
            yLabel='Power [kWh]'
            title='Pv Production vs. Eldemand [kWh]'
            data={getPvVsEldemandData(data.lineData)}
          />
        </Stack>
      )}

      {data?.kpis && (
        <Grid container alignItems={'center'} sx={{ mt: 3, width: '100%', height: '100%' }}>
          {data.kpis.map((kpi: any, index: any) => (
            <Grid key={index} item xs={12} sm={6} md={4}>
              <PieChart
                title={'Consumption Distribution ' + index}
                colors={{ scheme: 'set1' }}
                data={[
                  {
                    id: 'pv',
                    label: 'PV',
                    value: (kpi.self_consumption_pv * 100).toFixed(2),
                    color: '#ffcd38',
                  },
                  {
                    id: 'battery',
                    label: 'Battery',
                    value: ((kpi.self_consumption - kpi.self_consumption_pv) * 100).toFixed(2),
                    color: '#4dabf5',
                  },
                  {
                    id: 'grid',
                    label: 'Grid',
                    value: (100 - kpi.self_consumption * 100).toFixed(2),
                    color: '#f6685e',
                  },
                ]}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  )
}

export const MemoScenarioDashboard = React.memo(ScenarioDashboard)
